/* eslint-disable @typescript-eslint/no-namespace */
import * as dayjs from 'dayjs';
import { DynamicPricingSettings } from '../models/serializable/dynamic-pricing-settings.js';
import { EventPrivacyMode } from '../models/serializable/event-privacy-mode.js';
import { EventProfile } from '../models/serializable/event-profile.js';
import { ClassificationInfo, EventInfo, LocationInfo, PricingInfo, SaleState } from '../models/serializable/event-info.js';
import { EventUtils } from './event-utils.js';
import { isNotNullOrUndefined, isNullOrUndefined } from './null-safety.js';
import { isNullOrUndefinedOrEmpty } from './string.js';
import { TicketTemplateComponent } from '../components/events/ticket-template-component.js';
import { Price } from '../models/serializable/price.js';
import { PlaceDisplayMode } from '../models/serializable/place.js';
import { ITicketPool } from '../models/serializable/ticketing-state/ticket-pool.js';
import { BackstageTicketComponent } from '../components/ticket-template/backstage-ticket-component.js';
import { TicketTemplate, TicketTemplateScope, TicketTemplateStatus } from '../models/serializable/ticket-template';
import { SeatingLayout } from '../models/serializable/seating/seating-layout.js';
import { findAllSeatsOfSeatingLayout } from './seating-layout-utils.js';
import { EventFeeProvider } from '../models/serializable/application-fee/event-fee-provider.js';
import { EventSeatSelectionLock } from '../models/serializable/ticketing-state/event-seat-selection-lock.js';
import { TicketSupplementTemplate } from '../models/serializable/ticket-supplement-template.js';
import { EventTicketCapacityGroups } from '../models/serializable/event-ticket-capacity-groups.js';

export namespace EventInfoUtils {
  export function generate({
    eventProfile,
    dynamicPricingSettings,
    ticketSupplementTemplates,
    ticketPool,
    capacityGroups,
    seatingLayout,
    seatSelectionLock,
    now,
    partnerAppId,
    ignorePrivacyMode = false,
  }: {
      eventProfile: EventProfile,
      dynamicPricingSettings: DynamicPricingSettings | null,
      ticketSupplementTemplates: TicketSupplementTemplate[] | null,
      ticketPool: ITicketPool | null,
      capacityGroups?: EventTicketCapacityGroups | null,
      seatingLayout: SeatingLayout | null,
      seatSelectionLock: EventSeatSelectionLock | null,
      now: dayjs.Dayjs,
      partnerAppId?: string | null,
      ignorePrivacyMode?: boolean,
  }): EventInfo | null {
    const privacyMode = EventUtils.findPrivacyMode(eventProfile);
    if(!ignorePrivacyMode && (isNullOrUndefined(privacyMode) || privacyMode !== EventPrivacyMode.Public)) {
      return null;
    }

    const endDate = EventUtils.findEndDate(eventProfile);
    if(isNotNullOrUndefined(endDate) && endDate.isBefore(now)) {
      return null;
    }

    const webRoute = eventProfile.webRoute;
    if(isNullOrUndefined(webRoute)) {
      return null;
    }

    const localizedName = EventUtils.findName(eventProfile);
    if(isNullOrUndefined(localizedName)) {
      return null;
    }

    const startDate = EventUtils.findStartDate(eventProfile);
    if(isNullOrUndefined(startDate)) {
      return null;
    }

    const locationInfo = generateLocationInfo(eventProfile);
    if(isNullOrUndefined(locationInfo)) {
      return null;
    }

    const coverImage = EventUtils.findCoverImageLod0(eventProfile) ?? EventUtils.findCoverImage(eventProfile);
    if(isNullOrUndefinedOrEmpty(coverImage)) {
      return null;
    }

    const eventDateFormat = EventUtils.getEventDateFormat(eventProfile);
    const passEventOverrideLink = EventUtils.getPassEventOverrideLink(eventProfile);
    const thirdPartyEventUrl = EventUtils.getThirdPartyEventUrl(eventProfile);
    const freeEventUrl = EventUtils.getFreeEventUrl(eventProfile);

    const pricingInfo = findEventPricingInfo({
      eventProfile,
      dynamicPricingSettings,
      ticketSupplementTemplates,
      ticketPool,
      capacityGroups,
      seatingLayout,
      seatSelectionLock,
      now,
    });

    const classificationInfo = generateClassificationInfo(eventProfile, partnerAppId);

    return EventInfo.create({
      uniqueId: eventProfile.uniqueId,
      productionId: eventProfile.productionId,
      creationDate: eventProfile.creationDate,
      webRoute: webRoute,
      country: eventProfile.country,
      localizedName: localizedName,
      startDate: startDate,
      coverImage: coverImage,
      pricingInfo: pricingInfo,
      locationInfo: locationInfo,
      classificationInfo: classificationInfo,
      eventDateFormat: eventDateFormat,
      passEventOverrideLink: passEventOverrideLink,
      thirdPartyEventUrl: thirdPartyEventUrl,
      freeEventUrl: freeEventUrl,
    });
  }
}

function findEventPricingInfo({ eventProfile, dynamicPricingSettings, ticketSupplementTemplates, ticketPool, capacityGroups, seatingLayout, seatSelectionLock, now }: {
    eventProfile: EventProfile,
    dynamicPricingSettings: DynamicPricingSettings | null,
    ticketSupplementTemplates: TicketSupplementTemplate[] | null,
    ticketPool: ITicketPool | null,
    capacityGroups?: EventTicketCapacityGroups | null,
    seatingLayout: SeatingLayout | null,
    seatSelectionLock: EventSeatSelectionLock | null,
    now: dayjs.Dayjs,
}): PricingInfo | null {
  const ticketTemplateComponent = eventProfile.findComponentByTypeId<TicketTemplateComponent>(
      TicketTemplateComponent.TYPE_ID
  );

  const eventFeeProvider = new EventFeeProvider(eventProfile);

  if(isNullOrUndefined(ticketTemplateComponent)) {
    return null;
  }

  let tickets = ticketTemplateComponent.tickets
      .filter((x) => x.scope === TicketTemplateScope.Public);

  let remainingTicketCount: number | null = null;

  if(seatingLayout != null) {
    const configuredTicketTemplateIds = findConfiguredTicketTemplateIds(seatingLayout);
    const isConfiguredTemplate = (x: TicketTemplate) => configuredTicketTemplateIds.has(x.uniqueId);

    tickets = tickets.filter(isConfiguredTemplate);

    if(ticketPool != null) {
      const configuredSeatIds = new Set<number>();

      for(const seat of findAllSeatsOfSeatingLayout(seatingLayout)) {
        if(seat.tickets.some((templateId) => configuredTicketTemplateIds.has(templateId))) {
          configuredSeatIds.add(seat.uniqueId);
        }
      }

      const occupiedSeatIds = new Set<number>();

      const addIfConfigured = (seatId: number) => {
        if(configuredSeatIds.has(seatId)) {
          occupiedSeatIds.add(seatId);
        }
      };

      if(isNotNullOrUndefined(seatSelectionLock?.entries)) {
        for(const entry of seatSelectionLock.entries) {
          if(!entry.isExpired()) {
            for(const seatId of entry.seats) {
              addIfConfigured(seatId);
            }
          }
        }
      }

      const reservedSeats = ticketPool.getSeatingModule()?.reservedSeats;
      if(isNotNullOrUndefined(reservedSeats)) {
        for(const seatId of reservedSeats) {
          addIfConfigured(seatId);
        }
      }

      const openSeats = configuredSeatIds.size - occupiedSeatIds.size;
      const remainingCapacity = capacityGroups?.getTotalRemainingCapacity(tickets, ticketPool);
      remainingTicketCount = remainingCapacity?.isFinite ? Math.min(openSeats, remainingCapacity.value) : openSeats;
    }
  }

  if(tickets.length <= 0) {
    return null;
  }

  let lowestPrice: Price | null = null;
  let highestPrice: Price | null = null;
  let isEventSoldOut: boolean = true;
  let allTemplatesFuture: boolean = true;

  const isSoldOut = (ticket: TicketTemplate): boolean => {
    if(ticketPool == null) return false;

    const group = capacityGroups?.findGroup(ticket.uniqueId);
    if(group == null) return ticketPool.isSoldOut(ticket);

    const remaining = group.getRemainingCapacity(ticketPool);
    return remaining.isFinite && remaining.value === 0;
  };

  for(const ticket of tickets) {
    const isFutureTicket = now < ticket.dateSpan.from;
    if(!isFutureTicket) {
      allTemplatesFuture = false;
    }
    const isBackstageTicket = ticket.components.hasComponentByTypeId(BackstageTicketComponent.TYPE_ID);
    let ticketPrice = ticket.price;
    const ticketSupplementTemplate = ticketSupplementTemplates
        ?.find((ticketSupplementTemplate) => ticketSupplementTemplate.uniqueId === ticket.ticketSupplementTemplateId) ?? null;
    const ticketFee = eventFeeProvider
        .calculateFeeForTicketCartItem({ ticket: ticket, supplement: ticketSupplementTemplate, quantity: 1 });

    ticketPrice = ticketSupplementTemplate != null ?
      ticketPrice.add(ticketSupplementTemplate.price) :
      ticketPrice;

    ticketPrice = ticketPrice.add(ticketFee);

    if(isBackstageTicket) {
      const isAvailable = ticket.status === TicketTemplateStatus.Live &&
        isNotNullOrUndefined(ticketPool) &&
        !isSoldOut(ticket);
      if(!isAvailable) {
        continue;
      }

      isEventSoldOut = false;
      if(isNullOrUndefined(lowestPrice) || ticketPrice.value < lowestPrice.value) {
        lowestPrice = ticketPrice.clone();
      }

      if(isNullOrUndefined(highestPrice) || ticketPrice.value > highestPrice.value) {
        highestPrice = ticketPrice.clone();
      }
    }
    else {
      const ticketIsSoldOut = isSoldOut(ticket);
      const isAvailable = now >= ticket.dateSpan.from && now < ticket.dateSpan.to;

      if(ticket.isPurchasable && ticket.isPublic && isAvailable && !ticketIsSoldOut) {
        isEventSoldOut = false;
      }

      if(!ticket.isPurchasable || !ticket.isPublic || !isAvailable || ticketIsSoldOut) {
        continue;
      }

      const modifier = dynamicPricingSettings?.priceModifiers?.get(ticket.uniqueId);
      if(isNotNullOrUndefined(modifier)) {
        ticketPrice = ticketPrice.add(modifier);
      }

      if(isNullOrUndefined(lowestPrice) || ticketPrice.value < lowestPrice.value) {
        lowestPrice = ticketPrice.clone();
      }

      if(isNullOrUndefined(highestPrice) || ticketPrice.value > highestPrice.value) {
        highestPrice = ticketPrice.clone();
      }
    }
  }

  let saleState: SaleState;
  if(allTemplatesFuture) {
    saleState = SaleState.Upcoming;
  }
  else if(isEventSoldOut) {
    saleState = SaleState.SoldOut;
  }
  else {
    saleState = SaleState.Open;
  }

  return PricingInfo.create({
    saleState: saleState,
    remainingTicketCount: remainingTicketCount,
    minPrice: lowestPrice,
    maxPrice: highestPrice,
  });
}

function findConfiguredTicketTemplateIds(
    seatingLayout: SeatingLayout,
): Set<string> {
  const result = new Set<string>();

  for(const seat of findAllSeatsOfSeatingLayout(seatingLayout)) {
    for(const templateId of seat.tickets) {
      result.add(templateId);
    }
  }

  return result;
}

function generateLocationInfo(eventProfile: EventProfile): LocationInfo | null {
  const displayMode = EventUtils.findLocationDisplayMode(eventProfile) ?? PlaceDisplayMode.Minimal;
  const localizedName = EventUtils.findLocationName(eventProfile);
  if(isNullOrUndefined(localizedName)) {
    return null;
  }

  if(displayMode === PlaceDisplayMode.Minimal) {
    return LocationInfo.create({
      localizedName: localizedName,
      localizedAddress: null,
      localizedCity: null,
    });
  }

  const localizedCity = EventUtils.findCity(eventProfile);
  if(displayMode === PlaceDisplayMode.Approximate) {
    return LocationInfo.create({
      localizedName: localizedName,
      localizedCity: localizedCity,
      localizedAddress: null,
    });
  }

  const localizedAddress = EventUtils.findAddress(eventProfile);

  return LocationInfo.create({
    localizedName: localizedName,
    localizedAddress: localizedAddress,
    localizedCity: localizedCity,
  });
}

function generateClassificationInfo(eventProfile: EventProfile, partnerAppId?: string | null): ClassificationInfo {
  const classification = EventUtils.findClassification(eventProfile);
  const isFeatured = EventUtils.findFeaturedFlag(eventProfile, partnerAppId);
  const featuredScore = EventUtils.findFeaturedScore(eventProfile, partnerAppId);

  return ClassificationInfo.create({
    classification: classification ?? [],
    isFeatured: isFeatured ?? false,
    featuredScore: featuredScore ?? 0,
  });
}
